import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RESET_PATH, usePasswordReset } from '@/Coupons/hooks/use-password-reset';
import { SEO } from '@/components/SEO';
import {
  IconCheck,
  IconLoader4,
  IconLock,
  IconMail,
  IconMailOpened,
  IconRefresh,
} from '@tabler/icons-react';

/**
 * Two ways in:
 *  - Directly: the shopper enters their email and we email them a reset link.
 *  - From that link (`/forgot-password?nonce=…`): we exchange the nonce for a
 *    reset session and go straight to choosing a new password.
 */
type Step = 'contact' | 'sent' | 'verifying' | 'password' | 'success';

const STEPS = ['contact', 'sent', 'password'] as const;
const RESEND_COOLDOWN = 60;

/** Map each step onto the three-dot indicator. */
function indicatorIndex(step: Step): number {
  if (step === 'verifying') return 1;
  if (step === 'success') return STEPS.length;
  return STEPS.indexOf(step);
}

export default function ForgotPassword() {
  const navigate = useNavigate();
  const { requestReset, verifyNonce, setNewPassword, cancel } = usePasswordReset();

  // Read once on mount: the nonce is single-use and removed from the address
  // bar right away, so later renders must not look for it again.
  const [linkNonce] = useState(
    () => new URLSearchParams(window.location.search).get('nonce')?.trim() || null,
  );

  const [step, setStep] = useState<Step>(linkNonce ? 'verifying' : 'contact');
  const [contactInfo, setContactInfo] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const [resendCooldown, setResendCooldown] = useState(0);
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startCooldown = useCallback(() => {
    setResendCooldown(RESEND_COOLDOWN);
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          if (cooldownRef.current) clearInterval(cooldownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => {
    return () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
    };
  }, []);

  // Opened from the emailed link: exchange the nonce exactly once. The ref
  // guards against StrictMode's double-invoked effect, which would otherwise
  // spend the single-use nonce on the first call and fail the second.
  const exchangeStartedRef = useRef(false);
  const { mutateAsync: exchangeNonce } = verifyNonce;
  useEffect(() => {
    if (!linkNonce || exchangeStartedRef.current) return;
    exchangeStartedRef.current = true;

    // Drop the nonce from the address bar and history so it can't be
    // bookmarked, shared, or replayed from the back button.
    navigate(RESET_PATH, { replace: true });

    exchangeNonce(linkNonce)
      .then(() => setStep('password'))
      .catch((err: unknown) => {
        setError(
          err instanceof Error
            ? err.message
            : 'This reset link is invalid or has expired. Please request a new one.',
        );
        setStep('contact');
      });
  }, [linkNonce, exchangeNonce, navigate]);

  const sendLink = async (email: string) => {
    await requestReset.mutateAsync(email);
    startCooldown();
  };

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const email = contactInfo.trim();
    if (!email) {
      setError('Please enter the email address on your account');
      return;
    }
    if (!email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      await sendLink(email);
      setStep('sent');
    } catch (err: unknown) {
      setError(
        err instanceof Error
          ? err.message
          : 'Unable to send a reset link. Please check the email address and try again.',
      );
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || requestReset.isPending) return;
    setError(null);
    try {
      await sendLink(contactInfo.trim());
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Unable to resend the reset link.');
    }
  };

  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      await setNewPassword.mutateAsync(password);
      setStep('success');
    } catch (err: unknown) {
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to update password. Please try again.',
      );
    }
  };

  if (step === 'success') {
    return (
      <div className="min-h-screen bg-neutral-50 py-8 md:py-12">
        <SEO title="Password Reset" description="Reset your loyalty account password." />
        <div className="max-w-md mx-auto px-4">
          <div className="flex flex-col items-center justify-center py-12">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-6">
              <IconCheck className="h-10 w-10 text-green-600" />
            </div>
            <h1 className="text-xl font-bold text-foreground mb-2">
              Password Updated
            </h1>
            <p className="text-muted-foreground text-center mb-6">
              Your password has been reset successfully. You can now sign in with
              your new password.
            </p>
            <Button onClick={() => navigate('/coupons')} className="w-full">
              Done
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const current = indicatorIndex(step);

  return (
    <div className="min-h-screen bg-neutral-50 py-8 md:py-12">
      <SEO title="Reset Password" description="Reset your loyalty account password." />
      <div className="max-w-md mx-auto px-4">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground">Reset Password</h1>
        </div>

        {/* Step indicator */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  current === i
                    ? 'bg-primary text-white'
                    : current > i
                      ? 'bg-primary/20 text-primary'
                      : 'bg-gray-200 text-muted-foreground'
                }`}
              >
                {i + 1}
              </div>
              {i < STEPS.length - 1 && (
                <div
                  className={`w-8 h-0.5 ${current > i ? 'bg-primary/40' : 'bg-gray-200'}`}
                />
              )}
            </div>
          ))}
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg mb-6">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {step === 'contact' && (
          <form onSubmit={handleRequestReset} className="space-y-4">
            <div className="text-center mb-2">
              <h2 className="text-lg font-semibold text-foreground">
                Find Your Account
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Enter the email address on your account and we&apos;ll email you
                a link to reset your password.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="contactInfo">
                <IconMail className="h-3.5 w-3.5 mr-1.5" />
                Email Address
              </Label>
              <Input
                id="contactInfo"
                type="email"
                inputMode="email"
                autoComplete="email"
                autoCapitalize="none"
                placeholder="email@example.com"
                value={contactInfo}
                onChange={(e) => setContactInfo(e.target.value)}
                required
                autoFocus
              />
            </div>

            <p className="text-xs text-muted-foreground">
              The link expires in 15 minutes and can only be used once.
            </p>

            <Button
              type="submit"
              className="w-full"
              disabled={requestReset.isPending || !contactInfo.trim()}
            >
              {requestReset.isPending ? 'Sending...' : 'Send Reset Link'}
            </Button>
          </form>
        )}

        {step === 'sent' && (
          <div className="space-y-4">
            <div className="flex flex-col items-center text-center mb-2">
              <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <IconMailOpened className="h-7 w-7 text-primary" />
              </div>
              <h2 className="text-lg font-semibold text-foreground">
                Check Your Email
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                If an account exists for {contactInfo.trim()}, we sent it a link
                to reset your password. The link expires in 15 minutes. Check your
                spam folder if it doesn&apos;t arrive.
              </p>
            </div>

            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">Didn&apos;t get the email?</p>
              <button
                type="button"
                onClick={handleResend}
                disabled={resendCooldown > 0 || requestReset.isPending}
                className="flex items-center gap-1.5 text-sm font-medium text-primary disabled:text-muted-foreground disabled:cursor-not-allowed transition-colors"
              >
                <IconRefresh className="h-3 w-3" />
                {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Link'}
              </button>
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => {
                cancel();
                setStep('contact');
                setError(null);
              }}
            >
              Use a Different Email
            </Button>
          </div>
        )}

        {step === 'verifying' && (
          <div className="flex flex-col items-center text-center py-8" role="status">
            <IconLoader4 className="h-8 w-8 text-primary animate-spin mb-4" />
            <p className="text-sm text-muted-foreground">Checking your reset link...</p>
          </div>
        )}

        {step === 'password' && (
          <form onSubmit={handleSetPassword} className="space-y-4">
            <div className="text-center mb-2">
              <h2 className="text-lg font-semibold text-foreground">
                Create New Password
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Choose a new password for your account.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">
                <IconLock className="h-3.5 w-3.5 mr-1.5" />
                New Password
              </Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter new password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                required
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">
                <IconLock className="h-3.5 w-3.5 mr-1.5" />
                Confirm Password
              </Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                required
              />
              {confirmPassword && password !== confirmPassword && (
                <p className="text-xs text-red-500">Passwords do not match</p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={
                setNewPassword.isPending ||
                !password ||
                !confirmPassword ||
                password !== confirmPassword
              }
            >
              {setNewPassword.isPending ? 'Resetting...' : 'Reset Password'}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
